"use client";

/**
 * Root Route Error Page
 *
 * Next.js error.tsx pattern for the root route.
 * This component catches errors that occur anywhere in the application
 * and provides a user-friendly error UI with retry functionality.
 *
 * Features:
 * - Uses Next.js error.tsx convention (must be a Client Component)
 * - Root-level error boundary for all route errors
 * - Displays user-friendly error message
 * - Provides retry button to recover from errors
 * - Logs errors to console and Sentry
 * - Styled with Tailwind 4 with dark mode support
 *
 * @see https://nextjs.org/docs/app/api-reference/file-conventions/error
 */

import { useEffect } from "react";
import { cn } from "@/lib/utils";
import { Link } from "lucide-react";

/**
 * Error Page Props Interface
 */
interface ErrorPageProps {
  /** The error that was thrown */
  error: Error & { digest?: string };
  /** Function to reset the error boundary and retry rendering */
  reset: () => void;
}

/**
 * Root Error Page Component
 *
 * This component is automatically rendered by Next.js when an error
 * occurs in any route. It must be a Client Component ("use client")
 * because it uses useEffect and event handlers.
 */
export default function RootErrorPage({ error, reset }: ErrorPageProps) {
  /**
   * Log error details on mount
   * This runs only on the client side after the error occurs
   */
  useEffect(() => {
    // Log to console for debugging
    console.error("Root error caught by error.tsx:", error);

    // Log error digest if available (Next.js specific)
    if (error.digest) {
      console.error("Error digest:", error.digest);
    }

    // Report error to Sentry for monitoring
    // Sentry will only initialize if NEXT_PUBLIC_SENTRY_DSN is configured
    import("@/lib/sentry/client").then(({ captureException }) => {
      captureException(error, {
        tags: { route: "root", component: "RootErrorPage" },
        extra: { digest: error.digest },
      });
    });
  }, [error]);

  return (
    <div
      className={cn(
        "min-h-screen flex items-center justify-center p-6",
        "bg-background text-foreground",
      )}
    >
      <ErrorCard error={error} onReset={reset} />
    </div>
  );
}

/**
 * Error Card Component
 *
 * Displays the error information and provides retry functionality.
 * Separated for reusability and cleaner code.
 */
interface ErrorCardProps {
  error: Error & { digest?: string };
  onReset: () => void;
}

function ErrorCard({ error, onReset }: ErrorCardProps) {
  const isDevelopment = process.env.NODE_ENV === "development";

  return (
    <div
      className={cn(
        "max-w-lg w-full bg-card border rounded-xl shadow-lg p-8 text-center",
        "dark:bg-card dark:border-border",
      )}
      role="alert"
      aria-live="assertive"
    >
      {/* Error Icon */}
      <div
        className={cn(
          "mx-auto w-16 h-16 mb-6 flex items-center justify-center rounded-full",
          "bg-destructive/10 dark:bg-destructive/20",
        )}
      >
        <svg
          className="w-8 h-8 text-destructive"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>

      {/* Error Title */}
      <h1 className="text-2xl font-bold text-foreground mb-2">
        Something Went Wrong
      </h1>

      {/* Error Description */}
      <p className="text-muted-foreground mb-6">
        We apologize for the inconvenience. An unexpected error occurred while
        processing your request. Please try again or return to the homepage.
      </p>

      {/* Development Mode: Show Error Details */}
      {isDevelopment && (
        <div className="mb-6 text-left">
          <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4 overflow-auto">
            <p className="text-sm font-mono text-destructive mb-2">
              <strong>Error:</strong> {error.name}: {error.message}
            </p>
            {error.digest && (
              <p className="text-xs font-mono text-destructive/80 mb-2">
                <strong>Digest:</strong> {error.digest}
              </p>
            )}
            {error.stack && (
              <pre className="text-xs font-mono text-destructive/80 whitespace-pre-wrap">
                {error.stack.split("\n").slice(0, 5).join("\n")}
              </pre>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={onReset}
          className={cn(
            "inline-flex items-center justify-center px-6 py-2.5 rounded-lg",
            "bg-primary text-primary-foreground font-medium",
            "hover:bg-primary/90 transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
            "dark:focus:ring-offset-background",
          )}
          type="button"
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Try Again
        </button>

        <Link
          href="/"
          className={cn(
            "inline-flex items-center justify-center px-6 py-2.5 rounded-lg",
            "border border-input bg-background font-medium",
            "hover:bg-accent hover:text-accent-foreground transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "dark:focus:ring-offset-background",
          )}
        >
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
            />
          </svg>
          Go Home
        </Link>
      </div>

      {/* Support Information */}
      <div className="mt-6 pt-6 border-t border-border">
        <p className="text-sm text-muted-foreground">
          Need help? Contact us at{" "}
          <a
            href="mailto:support@truedemand.ai"
            className="text-primary hover:underline"
          >
            support@truedemand.ai
          </a>
        </p>
      </div>
    </div>
  );
}
